import { useRef } from "react";
import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Girih from "@/components/landing/Girih";
import HeroVideo from "@/components/landing/HeroVideo";
import { useLamp } from "@/hooks/useLamp";
import DailyVerse from "@/components/ui/DailyVerse";
import { SCHOLAR_ENABLED } from "@/lib/features";
import { useClarifierFilters, usePageView, useSeerahFilters, useWorldFilters } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1] as const;

/** Fades and rises into place once, when scrolled into view. */
function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ============================================
   Library Data
   ============================================ */

const library = [
  {
    title: "Seerah Explorer",
    description: "The life of Prophet Muhammad ﷺ, event by event, with the hadith and Quranic references behind each one.",
    to: "/seerah",
    stat: "13 events · 8 eras",
  },
  {
    title: "World Timeline",
    description: "Byzantium, Persia, China and more — what the rest of the world was doing while the Prophetic story unfolded.",
    to: "/timeline",
    stat: "20 events · 8 civilizations",
  },
  {
    title: "Quran Companion",
    description: "Read with tafsir from Ibn Kathir and al-Sa'di, thematic connections and full-text search.",
    to: "/quran",
    stat: "5 surahs · 24 ayahs",
  },
  {
    title: "Misconception Clarifier",
    description: "Common misconceptions about Islam, answered from the Quran and Sunnah, with every source cited.",
    to: "/clarifier",
    stat: "6 topics · 5 categories",
  },
  {
    title: "Isnad Explorer",
    description: "Follow a hadith's chain of narration from the great collectors back to the Prophet ﷺ himself.",
    to: "/isnad",
    stat: "13 narrators · 2 chains",
  },
  {
    title: "Learning Paths",
    description: "Guided courses that weave Quran, hadith, seerah and scholarship into one journey.",
    to: "/learning",
    stat: "1 path · 5 lessons",
  },
  {
    title: "Dhikr Companion",
    description: "Morning and evening adhkar from authenticated hadith, with a tap-to-count tasbeeh.",
    to: "/dhikr",
    stat: "7 adhkar · counter",
  },
];

/* ============================================
   Hero Section
   ============================================ */

/** Live content counts for the hero (blank until loaded rather than invented). */
function useLibraryStats() {
  const seerah = useSeerahFilters();
  const clarifier = useClarifierFilters();
  const world = useWorldFilters();
  const worldTotal = world.data?.civilizations.reduce((sum, c) => sum + c.count, 0);
  const fmt = (n: number | undefined) => (n === undefined ? "—" : n.toLocaleString());
  return [
    { label: "Seerah events", value: fmt(seerah.data?.totalEvents) },
    { label: "Misconceptions answered", value: fmt(clarifier.data?.totalMisconceptions) },
    { label: "World events", value: fmt(worldTotal) },
  ];
}

function Hero() {
  const stats = useLibraryStats();

  return (
    <section className="on-dark grain relative min-h-[100svh] flex flex-col overflow-hidden bg-[var(--color-night)] text-[var(--color-cream)]">
      {/* The dunya, seen from above — footage on the right, falling off into night */}
      <HeroVideo className="inset-x-0 -top-[16%] h-[70%] md:top-0 md:h-[72%] lg:inset-y-0 lg:left-auto lg:h-auto lg:w-[76%]" />
      {/* Fades that keep the copy on clean night */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,var(--color-night)_0%,var(--color-night)_40%,rgba(7,13,25,0.8)_60%,rgba(7,13,25,0.45)_78%,rgba(7,13,25,0.15)_92%)] lg:bg-[linear-gradient(90deg,var(--color-night)_0%,var(--color-night)_24%,rgba(7,13,25,0.6)_44%,transparent_64%)]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[var(--color-night)] to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[var(--color-night)]/80 to-transparent" />

      <div className="relative z-10 flex-1 flex flex-col w-full max-w-[1440px] mx-auto px-5 md:px-8 lg:px-12 pt-28 pb-10 md:pb-12">
        <div className="flex-1" />

        <div className="grid lg:grid-cols-12 gap-x-10 gap-y-8 items-end pb-10 md:pb-14">
          <div className="lg:col-span-8">
            <motion.p
              lang="ar"
              dir="rtl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.2 }}
              className="font-arabic text-[var(--color-gold)]/80 [text-shadow:0_2px_16px_rgba(0,0,0,0.7)] text-[22px] md:text-[28px] leading-none mb-6 md:mb-8 text-left"
            >
              بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ
            </motion.p>

            <h1 className="font-display font-normal text-[clamp(4.25rem,11.5vw,11.5rem)] leading-[0.86] tracking-[-0.035em]">
              <span className="line-mask">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
                >
                  Deen
                </motion.span>
              </span>{" "}
              <span className="line-mask">
                <motion.span
                  className="block whitespace-nowrap"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
                >
                  <em className="italic text-[var(--color-gold)] pr-[0.06em]">over</em> Dunya
                </motion.span>
              </span>
            </h1>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
            className="relative lg:col-span-4 lg:pb-4 max-w-[30rem]"
          >
            {/* Soft shade so the copy reads over the planet */}
            <div aria-hidden="true" className="pointer-events-none absolute -inset-x-16 -inset-y-14 -z-10 bg-[radial-gradient(closest-side,rgba(7,13,25,0.85)_50%,transparent)]" />
            <p className="text-[17px] md:text-[19px] leading-[1.55] text-[var(--color-cream)]/70">
              Islamic knowledge you can check. Every hadith graded, every ayah in context, every claim traced back to
              its source.
            </p>
            <div className="mt-8 flex flex-col min-[480px]:flex-row gap-3">
              <Link to="/seerah" className="btn-gold">
                Begin exploring
                <ArrowRight className="btn-arrow w-4 h-4" />
              </Link>
              <Link to="/timeline" className="btn-glass">
                View the timeline
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Live counts */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="border-t border-[var(--color-cream)]/10 py-5 md:py-6 flex items-center justify-between gap-6"
        >
          <dl className="flex flex-wrap gap-x-8 md:gap-x-12 gap-y-3">
            {stats.map((stat) => (
              <div key={stat.label} className="flex items-baseline gap-2.5">
                <dt className="sr-only">{stat.label}</dt>
                <dd className="font-display text-[22px] leading-none text-[var(--color-cream)] lining-nums tabular-nums">
                  {stat.value}
                </dd>
                <dd aria-hidden="true" className="text-[13px] text-[var(--color-cream)]/50">
                  {stat.label}
                </dd>
              </div>
            ))}
          </dl>
          <span className="hidden md:flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--color-cream)]/40">
            Scroll
            <span className="block w-10 h-px bg-[var(--color-cream)]/30" />
          </span>
        </motion.div>
      </div>
    </section>
  );
}

/* ============================================
   Library — the seven tools as an index
   ============================================ */

function Library() {
  return (
    <section className="sheet-paper sheet-lift py-28 md:py-40">
      <div className="max-w-[1440px] mx-auto px-5 md:px-8 lg:px-12 grid lg:grid-cols-12 gap-x-10 gap-y-14">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <span className="eyebrow">The library</span>
              <h2 className="mt-6 font-display font-normal text-[clamp(2.75rem,5.2vw,4.75rem)] leading-[0.98] tracking-[-0.03em]">
                Seven ways <em className="italic text-[var(--color-gold-dim)] dark:text-[var(--color-gold)]">in.</em>
              </h2>
              <p className="mt-6 text-[17px] leading-[1.6] text-[var(--paper-muted)] max-w-[24rem]">
                Every tool draws on the same verified sources — the Quran, sahih hadith and scholarly consensus — and
                each one links to the others.
              </p>
            </Reveal>
          </div>
        </div>

        <ol className="lg:col-span-8 border-b border-[var(--paper-line)]">
          {library.map((item, i) => (
            <li key={item.to} className="border-t border-[var(--paper-line)]">
              <Reveal delay={i * 0.06}>
                <Link
                  to={item.to}
                  className="group grid grid-cols-[2.5rem_1fr_auto] md:grid-cols-[3.5rem_1fr_auto] items-start gap-x-4 md:gap-x-6 py-7 md:py-9 transition-colors duration-300 hover:bg-[var(--paper-ink)]/[0.025] -mx-3 px-3 rounded-sm"
                >
                  <span className="pt-2.5 md:pt-3.5 text-[13px] font-semibold tabular-nums text-[var(--color-gold-dim)] dark:text-[var(--color-gold)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-display text-[clamp(1.6rem,2.7vw,2.5rem)] leading-[1.1] tracking-[-0.02em] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-1.5">
                      {item.title}
                    </span>
                    <span className="mt-2.5 block text-[15px] leading-[1.6] text-[var(--paper-muted)] max-w-[46ch]">
                      {item.description}
                    </span>
                    <span className="md:hidden mt-3 block text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--paper-muted)]/80">
                      {item.stat}
                    </span>
                  </span>
                  <span className="flex items-center gap-5 pt-1 md:pt-2">
                    <span className="hidden md:block text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--paper-muted)]/80 whitespace-nowrap">
                      {item.stat}
                    </span>
                    <span className="w-11 h-11 rounded-full border border-[var(--paper-line)] flex items-center justify-center transition-colors duration-300 group-hover:bg-[var(--color-gold)] group-hover:border-[var(--color-gold)] group-hover:text-[var(--color-midnight)]">
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:rotate-45" />
                    </span>
                  </span>
                </Link>
              </Reveal>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ============================================
   Two Worlds — parallel timeline preview
   ============================================ */

const parallels = [
  { year: "570", seerah: "Birth of Prophet Muhammad ﷺ", world: "Aksumite campaign on Makkah — the Year of the Elephant" },
  { year: "610", seerah: "First revelation in the Cave of Hira", world: "The Byzantine–Sassanid war rages across the Middle East" },
  { year: "622", seerah: "The Hijrah to Madinah", world: "Heraclius launches his counteroffensive against Persia" },
  { year: "627", seerah: "Battle of the Trench", world: "Battle of Nineveh — Heraclius defeats Persia" },
  { year: "630", seerah: "The conquest of Makkah", world: "Heraclius returns the True Cross to Jerusalem" },
];

function TwoWorlds() {
  const listRef = useRef<HTMLOListElement>(null);
  const { scrollYProgress } = useScroll({ target: listRef, offset: ["start 0.8", "end 0.55"] });
  const spine = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });

  return (
    <section className="on-dark sheet-night sheet-lift grain overflow-hidden py-28 md:py-40">
      <Girih className="opacity-40 -top-24 -left-40 w-[640px] h-[520px] [mask-image:radial-gradient(closest-side,#000,transparent)]" focal={[300, 300]} />
      <div className="relative max-w-[1200px] mx-auto px-5 md:px-8">
        <Reveal className="text-center">
          <span className="eyebrow eyebrow-center">Parallel timeline</span>
          <h2 className="mt-6 font-display font-normal text-[clamp(2.75rem,6vw,5.5rem)] leading-[0.98] tracking-[-0.03em]">
            Two worlds, <em className="italic text-[var(--color-gold)]">one story.</em>
          </h2>
          <p className="mt-6 mx-auto max-w-[36rem] text-[17px] leading-[1.6] text-[var(--color-cream)]/60">
            While the Prophet ﷺ was building a new community, empires were rising and falling around it. See how the
            two stories meet.
          </p>
        </Reveal>

        <div className="mt-20 md:mt-24 grid grid-cols-2 md:grid-cols-[1fr_8rem_1fr] text-[11px] font-semibold uppercase tracking-[0.22em] pb-6 border-b border-[var(--color-cream)]/10">
          <span className="md:text-right text-[var(--color-emerald-light)]">The Seerah</span>
          <span className="hidden md:block text-center text-[var(--color-gold)]/60">Year CE</span>
          <span className="text-right md:text-left text-[var(--color-cream)]/45">The wider world</span>
        </div>

        <ol ref={listRef} className="relative">
          {/* Spine */}
          <span aria-hidden="true" className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-[var(--color-cream)]/10" />
          <motion.span
            aria-hidden="true"
            style={{ scaleY: spine }}
            className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 origin-top bg-gradient-to-b from-[var(--color-gold)] to-[var(--color-gold)]/40"
          />
          {parallels.map((row, i) => (
            <li key={row.year} className="border-b border-[var(--color-cream)]/[0.07] md:border-0">
              <Reveal delay={i * 0.05} className="grid grid-cols-2 md:grid-cols-[1fr_8rem_1fr] items-center gap-x-6 gap-y-3 py-7 md:py-9">
                <span className="col-span-2 md:col-span-1 md:order-2 flex md:justify-center">
                  <span className="relative font-display text-[clamp(1.75rem,3.2vw,3rem)] leading-none text-[var(--color-gold)] lining-nums tabular-nums md:bg-[var(--color-night)] md:px-3 md:py-2">
                    {row.year}
                  </span>
                </span>
                <span className="md:order-1 md:text-right text-[15px] md:text-[18px] leading-[1.45] text-[var(--color-cream)]">
                  {row.seerah}
                </span>
                <span className="md:order-3 text-right md:text-left text-[15px] md:text-[18px] leading-[1.45] text-[var(--color-cream)]/55">
                  {row.world}
                </span>
              </Reveal>
            </li>
          ))}
        </ol>

        <Reveal className="mt-16 text-center">
          <Link to="/timeline" className="btn-gold">
            Explore the full timeline
            <ArrowRight className="btn-arrow w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ============================================
   Method
   ============================================ */

const principles = [
  {
    numeral: "i.",
    title: "Isnad verification",
    description:
      "Every hadith is traced through its chain of narration, and each narrator is weighed by the classical standards of scholars like Ibn Hajar and al-Dhahabi.",
  },
  {
    numeral: "ii.",
    title: "Cross-reference",
    description:
      "Claims are checked against the Quran, several hadith collections and scholarly consensus. Where scholars differ, we say so rather than hide it.",
  },
  {
    numeral: "iii.",
    title: "Source attribution",
    description:
      "Everything links back to its original source — book, chapter and verse — so you never have to take our word for it.",
  },
];

function Method() {
  return (
    <section className="sheet-paper pb-28 md:pb-40">
      <div className="max-w-[1440px] mx-auto px-5 md:px-8 lg:px-12">
        <div className="border-t border-[var(--paper-line)] pt-20 md:pt-28 grid lg:grid-cols-12 gap-x-10 gap-y-14">
          <Reveal className="lg:col-span-5">
            <span className="eyebrow">Our approach</span>
            <h2 className="mt-6 font-display font-normal text-[clamp(2.5rem,4.6vw,4.25rem)] leading-[1] tracking-[-0.03em]">
              Scholarship, <em className="italic text-[var(--color-gold-dim)] dark:text-[var(--color-gold)]">not speculation.</em>
            </h2>
            <Link
              to="/methodology"
              className="group mt-8 inline-flex items-center gap-2 text-[15px] font-semibold text-[var(--paper-ink)] border-b border-[var(--paper-ink)]/25 pb-1 hover:border-[var(--color-gold)] transition-colors"
            >
              How we work
              <ArrowRight className="btn-arrow w-4 h-4" />
            </Link>
          </Reveal>

          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-10 lg:gap-8">
            {principles.map((p, i) => (
              <Reveal key={p.numeral} delay={i * 0.1}>
                <span className="block font-display italic text-[44px] leading-none text-[var(--color-gold-dim)] dark:text-[var(--color-gold)]">
                  {p.numeral}
                </span>
                <h3 className="mt-6 font-display text-[24px] leading-[1.2] tracking-[-0.01em]">{p.title}</h3>
                <p className="mt-3 text-[15.5px] leading-[1.65] text-[var(--paper-muted)]">{p.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================
   Closing invitation
   ============================================ */

function Closing() {
  const sectionRef = useRef<HTMLElement>(null);
  const lampRef = useRef<HTMLDivElement>(null);
  useLamp(sectionRef, lampRef);

  return (
    <section ref={sectionRef} className="on-dark sheet-night sheet-lift grain overflow-hidden py-32 md:py-44">
      {/* The lattice, lit wherever the visitor looks */}
      <Girih lampRef={lampRef} className="inset-0 [--fx:50%] [--fy:100%]" focal={[660, 1080]} />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_34%_38%_at_50%_48%,var(--color-night)_40%,rgba(7,13,25,0.75)_68%,transparent)]" />
      <div className="relative max-w-[1100px] mx-auto px-5 md:px-8 text-center">
        <Reveal>
          <h2 className="font-display font-normal text-[clamp(2.75rem,7vw,6.5rem)] leading-[0.95] tracking-[-0.035em]">
            Ask a question.
            <br />
            <em className="italic text-[var(--color-gold)]">Check the answer.</em>
          </h2>
          <p className="mt-8 mx-auto max-w-[34rem] text-[17px] md:text-[18px] leading-[1.6] text-[var(--color-cream)]/60">
            The Research Assistant answers only from the verified sources in this library, with citations you can follow.
            {!SCHOLAR_ENABLED && " It's coming soon — until then, the Clarifier answers the questions people ask most."}
          </p>
          <div className="mt-10 flex justify-center">
            {SCHOLAR_ENABLED ? (
              <Link to="/scholar" className="btn-gold">
                Ask a question
                <ArrowRight className="btn-arrow w-4 h-4" />
              </Link>
            ) : (
              <Link to="/clarifier" className="btn-gold">
                Explore common questions
                <ArrowRight className="btn-arrow w-4 h-4" />
              </Link>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ============================================
   Landing Page
   ============================================ */

export default function LandingPage() {
  useSEO();
  usePageView("landing");
  return (
    <main className="overflow-x-clip">
      <Hero />
      <Library />
      <TwoWorlds />
      <DailyVerse />
      <Method />
      <Closing />
    </main>
  );
}
